import { Component, computed, DestroyRef, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { FormGroup, FormControl, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DynamicRouteHandlingService, GenericDropdownComponent, InputFieldComponent } from '@zayan-erp/shared';
import { signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TaxRatesService, TaxRateDto } from '../../services/tax-rates.service';
import { TaxAuthorityService } from '../../services/tax-authority.service';
import { TaxCategoriesService } from '../../services/tax-categories.service';
import { tap } from 'rxjs';
import { of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';

@Component({
  selector: 'lib-tax-rates-form',
  imports: [InputFieldComponent, FormsModule, ReactiveFormsModule, GenericDropdownComponent, DatePickerModule],
  templateUrl: './tax-rates-form.component.html',
  styleUrl: './tax-rates-form.component.scss'
})
export class TaxRatesFormComponent {
  #activatedRoute = inject(ActivatedRoute);
  #taxRatesService = inject(TaxRatesService);
  #taxAuthorityService = inject(TaxAuthorityService);
  #taxCategoriesService = inject(TaxCategoriesService);
  #router = inject(Router);
  #dynamicRouteHandling = inject(DynamicRouteHandlingService);
  #destroyRef = inject(DestroyRef);

  constructor() {
    this.#dynamicRouteHandling.submitButton.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.submit());
  }

  selectedTaxRateId = signal<string | null>(this.#activatedRoute.snapshot.paramMap.get('id'));
  editRxResource = rxResourceFixed({
    params: () => ({ id: this.selectedTaxRateId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (params.id) {
        return this.#taxRatesService.getTaxRateById(params.id).pipe(
          tap(taxRate => {
            this.editForm(taxRate);
          })
        );
      }
      return of(null);
    }
  });

  editForm(taxRate: TaxRateDto) {
    const effectiveDate = taxRate.effectiveDate ? new Date(taxRate.effectiveDate) : null;
    this.taxRateForm.patchValue({
      taxAuthorityId: taxRate.taxAuthorityId,
      taxCategoryId: taxRate.taxCategoryId,
      effectiveDate: effectiveDate,
      rate: taxRate.rate
    });
  }

  taxRateForm = new FormGroup({
    taxAuthorityId: new FormControl<string | null>(
      this.#activatedRoute.snapshot.queryParamMap.get('taxAuthorityId') ?? null,
      [Validators.required]
    ),
    taxCategoryId: new FormControl<string | null>(
      this.#activatedRoute.snapshot.queryParamMap.get('taxCategoryId') ?? null
    ),
    effectiveDate: new FormControl<Date | null>(null),
    rate: new FormControl<number | null>(null, [Validators.min(0), Validators.max(999)])
  });

  taxAuthorities = toSignal(this.#taxAuthorityService.readTaxAuthorities(), { initialValue: [] });
  taxCategories = toSignal(this.#taxCategoriesService.readTaxCategories(), { initialValue: [] });

  taxAuthoritiesList = computed(() =>
    this.taxAuthorities().map(authority => ({
      id: authority.id,
      firstLabel: authority.name,
      secondLabel: authority.code
    }))
  );

  taxCategoriesList = computed(() =>
    this.taxCategories().map(category => ({
      id: category.id,
      firstLabel: category.description || category.code,
      secondLabel: category.code
    }))
  );

  submit() {
    if (this.taxRateForm.invalid) {
      this.taxRateForm.markAllAsTouched();
      return;
    }

    const formValue = this.taxRateForm.value;
    const payload = {
      taxAuthorityId: formValue.taxAuthorityId!,
      taxCategoryId: formValue.taxCategoryId!,
      effectiveDate: formValue.effectiveDate ? formValue.effectiveDate.toISOString() : null,
      rate: formValue.rate!
    };

    if (this.selectedTaxRateId()) {
      this.#taxRatesService.updateTaxRate(this.selectedTaxRateId()!, payload).subscribe({
        next: () => this.afterSubmitSuccess()
      });
    } else {
      this.#taxRatesService.createTaxRate(payload).subscribe({
        next: () => this.afterSubmitSuccess()
      });
    }
  }

  afterSubmitSuccess() {
    this.taxRateForm.reset();
    this.#router.navigate([this.#activatedRoute.snapshot.data['returnRoute']], { queryParamsHandling: 'preserve' });
    this.#dynamicRouteHandling.reload.next(!this.#dynamicRouteHandling.reload.value);
  }
}
